import React, { useCallback, useRef, useState } from "react";
import { clientsCampaignImageUploadApi, clientsCampaignPreviewApi, clientsCampaignSaveApi } from "../../api/organizerClientsApi";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import CanalWhatsapp from "../recordatorios/CanalWhatsapp";
import { CAMPOS_WHATSAPP_CAMPANA, usePuedeCrearPlantillas } from "../recordatorios/recordatorios";
import { DIAS_ANTES, DIAS_SIN_VENIR, HORAS, MAX_ASUNTO, MAX_IMAGENES, MAX_TEXTO, TIPOS_IMAGEN, campanaAForm, faltaCampana, faltaImagen, formACampana, moverImagen } from "./campanas";
import { errorDeApi } from "./clientesUtils";
import useCarga from "./useCarga";
import "../recordatorios/recordatorios.css";

/* One campaign of Clientes > Campañas (birthday or win-back): switch, send time, when, channels and who qualifies today.
   `campana` is the server campaign; saving sends the whole form to PUT /campaigns/:type. */

const TEXTOS = {
  birthday: { titulo: "Cumpleaños", resumen: "Saluda a cada cliente en su cumpleaños." },
  winback: { titulo: "Te extrañamos", resumen: "Invita a volver a quien hace tiempo no viene." },
};

const OPCIONES_ANTES = DIAS_ANTES.map((value) => ({
  value,
  label: value === 0 ? "El día del cumpleaños" : value === 1 ? "1 día antes" : `${value} días antes`,
}));
const OPCIONES_SIN_VENIR = DIAS_SIN_VENIR.map((value) => ({ value, label: `${value} días` }));

const cuandoSale = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("es-AR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
};

const VistaPrevia = ({ vista, cargando, error, onReintentar }) => {
  if (cargando && !vista) return <p className="fc-hint" role="status">Calculando a quién le llega…</p>;
  if (error && !vista) {
    return (
      <div className="fc-error" role="alert">
        {error.message}{" "}
        <button type="button" className="zr-btn zr-btn--soft" onClick={onReintentar}>Reintentar</button>
      </div>
    );
  }
  if (!vista) return null;
  const sample = Array.isArray(vista.sample) ? vista.sample : [];
  const cuando = cuandoSale(vista.nextRunAt);
  return (
    <div className="zr-info" role="status">
      <span>
        <strong>Hoy califican {vista.eligibleToday} {vista.eligibleToday === 1 ? "cliente" : "clientes"}.</strong>
        {cuando && <> Próximo envío: {cuando}.</>}
        {sample.length > 0 && <> Por ejemplo: {sample.map((s) => s.name).join(", ")}.</>}
      </span>
    </div>
  );
};

const CampanaCard = ({ campana, local, onGuardada }) => {
  const tipo = campana.type;
  const { titulo, resumen } = TEXTOS[tipo];
  const puedeCrear = usePuedeCrearPlantillas();
  const [form, setForm] = useState(() => campanaAForm(campana));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [guardada, setGuardada] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [errorImagen, setErrorImagen] = useState("");
  const inputImagen = useRef(null);
  const preview = useCarga(() => clientsCampaignPreviewApi(tipo), tipo, { mensaje: "No pudimos calcular a quién le llega." });

  const cambiar = (parche) => {
    setGuardada(false);
    setForm((f) => ({ ...f, ...parche }));
  };
  const cambiarEmail = (parche) => cambiar({ email: { ...form.email, ...parche } });
  const subirImagen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const falta = faltaImagen(file);
    if (falta) {
      setErrorImagen(falta);
      return;
    }
    setErrorImagen("");
    setSubiendo(true);
    try {
      const res = await clientsCampaignImageUploadApi(file);
      const url = res?.data?.isSuccess === true ? res.data.info?.url : "";
      if (!url) throw new Error("sin url");
      setGuardada(false);
      // Functional update: the form may change while the upload is in flight.
      setForm((f) => (f.email.images.length >= MAX_IMAGENES ? f : { ...f, email: { ...f.email, images: [...f.email.images, { url, alt: "" }] } }));
    } catch (err) {
      const { status, codigo, message } = errorDeApi(err, "No pudimos subir la imagen. Probá de nuevo.");
      setErrorImagen(codigo === "campaign_image_invalid" || status === 400 ? "La imagen no es válida: usá JPG, PNG, WEBP o GIF de hasta 2 MB." : message);
    } finally {
      setSubiendo(false);
    }
  };
  const cambiarImagenes = (images) => cambiarEmail({ images });
  // Stable on purpose: the WhatsApp box calls it from effects.
  const cambiarWhatsapp = useCallback((whatsapp) => {
    setGuardada(false);
    setForm((f) => ({ ...f, whatsapp }));
  }, []);

  const guardar = async () => {
    const falta = faltaCampana(form);
    if (falta) {
      setError(falta);
      return;
    }
    setError("");
    setGuardando(true);
    try {
      const res = await clientsCampaignSaveApi(tipo, formACampana(form));
      const info = res?.data?.isSuccess === true ? res.data.info : null;
      if (info) setForm(campanaAForm({ ...info, type: tipo }));
      setGuardada(true);
      onGuardada?.();
      preview.reload();
    } catch (e) {
      const { status, message } = errorDeApi(e, "No pudimos guardar la campaña. Probá de nuevo.");
      setError(status === 403 ? "No tenés permiso para editar campañas." : message);
    } finally {
      setGuardando(false);
    }
  };

  const id = `camp-${tipo}`;
  return (
    <section className="zr-panel fc-campana" aria-label={`Campaña ${titulo}`}>
      <div className="fc-page__head">
        <div>
          <h3 className="zr-subhead__title">{titulo}</h3>
          <p className="fc-hint">{resumen}</p>
        </div>
        <input
          type="checkbox"
          className="zr-switch"
          aria-label={`Activar ${titulo}`}
          checked={form.enabled}
          onChange={() => cambiar({ enabled: !form.enabled })}
        />
      </div>

      <div className="zr-info" role="note">
        <span>Solo se envía a clientes que aceptaron recibir novedades.</span>
      </div>

      <VistaPrevia vista={preview.data} cargando={preview.loading} error={preview.error} onReintentar={preview.reload} />

      <div className="fc-campana__datos">
        <div className="zr-field">
          <span id={`${id}-hora-label`} className="zr-field__label">Hora de envío</span>
          <OrganizerPillSelect
            id={`${id}-hora`}
            labelledBy={`${id}-hora-label`}
            value={form.sendHour}
            onChange={(v) => cambiar({ sendHour: Number(v) })}
            options={HORAS}
          />
        </div>
        {tipo === "birthday" ? (
          <div className="zr-field">
            <span id={`${id}-cuando-label`} className="zr-field__label">Cuándo</span>
            <OrganizerPillSelect
              id={`${id}-cuando`}
              labelledBy={`${id}-cuando-label`}
              value={form.daysBefore}
              onChange={(v) => cambiar({ daysBefore: Number(v) })}
              options={OPCIONES_ANTES}
            />
          </div>
        ) : (
          <div className="zr-field">
            <span id={`${id}-dias-label`} className="zr-field__label">Si no vino hace</span>
            <OrganizerPillSelect
              id={`${id}-dias`}
              labelledBy={`${id}-dias-label`}
              value={form.inactiveDays}
              onChange={(v) => cambiar({ inactiveDays: Number(v) })}
              options={OPCIONES_SIN_VENIR}
            />
            <small className="fc-hint">Se envía cuando el cliente cumple esos días sin venir (solo cuentan las visitas marcadas como Presente). No se envía si ya tiene una reserva próxima.</small>
          </div>
        )}
      </div>

      <div className="rwa-canales">
        <div className="zr-box">
          <div className="zr-row-toggle">
            <div className="zr-row-toggle__txt">
              <div className="t rwa-t">Email</div>
              <div className="h">Le llega al email de la ficha. El link para darse de baja se agrega solo.</div>
            </div>
            <input
              type="checkbox"
              className="zr-switch"
              aria-label={`Email de ${titulo}`}
              checked={form.email.enabled}
              onChange={() => cambiarEmail({ enabled: !form.email.enabled })}
            />
          </div>
          {form.email.enabled && (
            <>
              <div className="zr-field">
                <label className="zr-field__label" htmlFor={`${id}-asunto`}>Asunto</label>
                <input
                  id={`${id}-asunto`}
                  className="zr-input"
                  value={form.email.subject}
                  maxLength={MAX_ASUNTO}
                  onChange={(e) => cambiarEmail({ subject: e.target.value })}
                />
              </div>
              <div className="zr-field">
                <label className="zr-field__label" htmlFor={`${id}-texto`}>Texto del email</label>
                <textarea
                  id={`${id}-texto`}
                  className="zr-textarea"
                  rows={5}
                  value={form.email.body}
                  maxLength={MAX_TEXTO}
                  onChange={(e) => cambiarEmail({ body: e.target.value })}
                />
                <span className={`fc-counter${form.email.body.length >= MAX_TEXTO ? " fc-counter--over" : ""}`}>
                  {form.email.body.length} / {MAX_TEXTO}
                </span>
              </div>
              <div className="zr-field">
                <span className="zr-field__label">Imágenes</span>
                {form.email.images.length > 0 && (
                  <ul className="fc-imagenes">
                    {form.email.images.map((img, i) => (
                      <li key={img.url} className="fc-imagenes__item">
                        <img className="fc-imagenes__thumb" src={img.url} alt={img.alt || `Imagen ${i + 1}`} />
                        <input
                          className="zr-input"
                          aria-label={`Texto alternativo de la imagen ${i + 1}`}
                          placeholder="Descripción (opcional)"
                          maxLength={120}
                          value={img.alt}
                          onChange={(e) => cambiarImagenes(form.email.images.map((x, j) => (j === i ? { ...x, alt: e.target.value } : x)))}
                        />
                        <div className="fc-imagenes__acciones">
                          <button type="button" className="zr-btn zr-btn--soft" aria-label={`Subir imagen ${i + 1}`} disabled={i === 0} onClick={() => cambiarImagenes(moverImagen(form.email.images, i, -1))}>↑</button>
                          <button type="button" className="zr-btn zr-btn--soft" aria-label={`Bajar imagen ${i + 1}`} disabled={i === form.email.images.length - 1} onClick={() => cambiarImagenes(moverImagen(form.email.images, i, 1))}>↓</button>
                          <button type="button" className="zr-btn zr-btn--soft" aria-label={`Quitar imagen ${i + 1}`} onClick={() => cambiarImagenes(form.email.images.filter((_, j) => j !== i))}>Quitar</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <input
                  ref={inputImagen}
                  type="file"
                  hidden
                  data-testid={`${id}-archivo`}
                  accept={TIPOS_IMAGEN.join(",")}
                  onChange={subirImagen}
                />
                <div>
                  <button
                    type="button"
                    className="zr-btn zr-btn--soft"
                    disabled={subiendo || form.email.images.length >= MAX_IMAGENES}
                    onClick={() => inputImagen.current?.click()}
                  >
                    {subiendo ? "Subiendo…" : "Agregar imagen"}
                  </button>
                </div>
                <small className="fc-hint">
                  {form.email.images.length >= MAX_IMAGENES
                    ? "Llegaste al máximo de 3 imágenes. Quitá una para agregar otra."
                    : "Se muestran dentro del mail, debajo del texto. Hasta 3 imágenes de 2 MB."}
                </small>
                {errorImagen && <div className="fc-error" role="alert">{errorImagen}</div>}
              </div>
            </>
          )}
        </div>
        <CanalWhatsapp
          whatsapp={form.whatsapp}
          onChange={cambiarWhatsapp}
          local={local}
          puedeCrear={puedeCrear}
          campos={CAMPOS_WHATSAPP_CAMPANA}
          idBase={id}
          etiqueta={`WhatsApp de ${titulo}`}
          pedido="campana"
          categoria="MARKETING"
        />
      </div>

      {error && <div className="fc-error" role="alert">{error}</div>}
      {guardada && !error && <p className="fc-hint" role="status">Campaña guardada.</p>}
      <div className="fc-actions">
        <button type="button" className="zr-btn zr-btn--dark" onClick={guardar} disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </section>
  );
};

export default CampanaCard;
